import {
  RuntimeStore,
  RuntimeEvent,
  RuntimeEffect,
  RuntimeWatch,
  RuntimeOn,
  RuntimeForward,
  RuntimeSample
} from '../runtime/units'
import {JSON_KEYS_MAX} from '../env'

export function generateEventHighlight({name}: RuntimeEvent) {
  return [
    l.syntax('const'),
    l.space(),
    l.variable(name),
    l.space(),
    l.syntax('='),
    l.space(),
    l.method('createEvent'),
    l.default('()'),
    l.newLine()
  ]
}
export function generateEffectHighlight({name, handler}: RuntimeEffect) {
  const result = [
    l.syntax('const'),
    l.space(),
    l.variable(name),
    l.space(),
    l.syntax('='),
    l.space(),
    l.method('createEffect'),
    l.default('('),
    l.default('{'),
    l.newLine(),
    {type: 'space', value: '  '},
    l.default('handler'),
    l.syntax(':'),
    l.space()
  ]
  processValue(result, handler)
  result.push(l.newLine(), l.default('}'), l.default(')'), l.newLine())
  return result
}
export function generateStoreHighlight({
  name,
  initialValue,
  type
}: RuntimeStore) {
  const result = [
    l.syntax('const'),
    l.space(),
    l.variable(name),
    l.space(),
    l.syntax('='),
    l.space(),
    l.method('createStore'),
    l.default('<'),
    l.variable(type),
    l.default('>'),
    l.default('(')
  ]

  processValue(result, initialValue)
  result.push(l.default(')'), l.newLine())
  return result
}
export function generateWatchHighlight({owner, sources}: RuntimeWatch) {
  return [
    l.variable(owner),
    l.default('.'),
    l.method('watch'),
    l.default('('),
    l.default(sources),
    l.default(')'),
    l.newLine()
  ]
}
export function generateOnHighlight({source, clock, sources}: RuntimeOn) {
  return [
    l.variable(source),
    l.default('.'),
    l.method('on'),
    l.default('('),
    l.default(clock),
    l.default(','),
    l.space(),
    l.default(sources),
    l.default(')'),
    l.newLine()
  ]
}
export function generateForwardHighlight({source, target}: RuntimeForward) {
  return [
    l.method('forward'),
    l.default('('),
    ...object({from: source, to: target}),
    l.default(')'),
    l.newLine()
  ]
}
export function generateSampleHighlight({
  source,
  clock,
  target
}: RuntimeSample) {
  return [
    l.method('sample'),
    l.default('('),
    ...object({clock, source, target}),
    l.default(')'),
    l.newLine()
  ]
}
const line = (key, value) => [
  {
    type: 'space',
    value: '  '
  },
  l.default(key),
  l.syntax(':'),
  l.space(),
  l.default(value),
  l.default(','),
  l.newLine()
]
const object = shape => {
  const result = []
  result.push(l.default('{'), l.newLine())
  for (const key in shape) {
    result.push(...line(key, shape[key]))
  }
  result.push(l.default('}'))
  return result
}
export function generateValueHighlight(value) {
  const result = []
  processValue(result, value)
  return result
}
const l = {
  default: value => ({type: 'default', value}),
  syntax: value => ({type: 'syntax', value}),
  newLine: () => ({type: 'space', value: `\n`}),
  space: () => ({type: 'space', value: ' '}),
  method: value => ({type: 'method', value}),
  variable: value => ({type: 'variable', value})
}

let level = -1
function newLine(sameLevel = true) {
  return {
    type: 'space',
    value: `\n${Array(level + (sameLevel ? 0 : 1))
      .fill(' ')
      .join('')}`
  }
}
function processValue(result, value) {
  level += 1
  if (Object(value) !== value) {
    switch (typeof value) {
      case 'object':
        result.push(l.variable('null'))
        break
      case 'undefined':
        result.push(l.variable('undefined'))
        break
      case 'number':
        //NOTE pass-through case
        if (value < 0) {
          result.push(l.syntax('-'))
          value = Math.abs(value)
        }
      case 'string':
        result.push({
          type: typeof value,
          value: value.toString()
        })
        break
      case 'symbol':
      case 'boolean':
        result.push(l.variable(value.toString()))
        break
      default:
        result.push({
          type: 'string',
          value: 'UNSUPPORTED'
        })
    }
  } else if (Array.isArray(value)) {
    result.push(l.default('['))
    const useNewLines = value.length > JSON_KEYS_MAX
    if (useNewLines) result.push(newLine(false))
    for (let i = 0; i < value.length; i++) {
      const item = value[i]
      processValue(result, item)
      if (i !== value.length - 1) {
        if (useNewLines) {
          result.push(
            {
              type: 'invisible',
              value: ','
            },
            newLine(false)
          )
        } else {
          result.push(l.default(','), l.space())
        }
      } else {
        if (useNewLines) {
          result.push(newLine(true))
        }
      }
    }
    result.push(l.default(']'))
  } else {
    if (typeof value === 'function') {
      const text = value.toString()
      const lines = text.split(`\n`)
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        result.push(l.default(line))
        if (i !== lines.length - 1) {
          result.push(l.newLine())
        }
      }
    } else {
      result.push(l.default('{'))
      const keys = Object.keys(value)
      const useNewLines = keys.length > JSON_KEYS_MAX
      if (useNewLines) result.push(newLine(false))
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i]
        result.push(l.default(key), l.syntax(':'), l.space())
        processValue(result, value[key])
        if (i !== keys.length - 1) {
          if (useNewLines) {
            result.push(
              {
                type: 'invisible',
                value: ','
              },
              newLine(false)
            )
          } else {
            result.push(l.default(','), l.space())
          }
        } else {
          if (useNewLines) {
            result.push(newLine(true))
          }
        }
      }
      result.push(l.default('}'))
    }
  }
  level -= 1
}
